import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, createGridDropDownCommitAfter, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { useKSK2110_R01Store } from "./Store_KSK2110_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { KSK2110_R01InspectDto } from "./Dto_KSK2110_R01";
import { Info_KSK2110_R01 } from "./Info_KSK2110_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { getAllWorkPlaces } from "src/apis/workPlaceApi";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { getAllWarehouses } from "src/apis/warehouseApi";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";

const COLUMN_STATE_KEY = 'KSK2110_R01-inspect-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: [''],
	insertedDisabledCells: [''],
	commonDisabledCells: [''],
	
	pointerCells: ['badQty'],
	insertedPointerCells: [''],
	commonPointerCells: [''],

	alignLeftCells: [''],
};

interface Props {
	gridRef: React.RefObject<AgGridReact<KSK2110_R01InspectDto> | null>;
	defaultKSK2110_R01InspectRow: KSK2110_R01InspectDto;
}

export const InspectTable_KSK2110_R01 = (props: Props) => {

	const { gridRef, defaultKSK2110_R01InspectRow } = props;

	const { 
			KSK2110_R01InspectList, setKSK2110_R01InspectList, 
			editKSK2110_R01,
			selectedKSK2110_R01InspectList, setSelectedKSK2110_R01InspectList, removeSelectedKSK2110_R01InspectList, addSelectedKSK2110_R01InspectList, initSelectedKSK2110_R01InspectList,
			deletedKSK2110_R01InspectList, setDeletedKSK2110_R01InspectList, addDeletedKSK2110_R01InspectList, removeDeletedKSK2110_R01InspectList,
			insertKSK2110_R01InspectList, updateKSK2110_R01InspectList, removeKSK2110_R01InspectList,
			inspectColumnState, setInspectColumnState,
			originalKSK2110_R01InspectList, setOriginalKSK2110_R01InspectList,
		} = useKSK2110_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { faultyValueList } = useCommonCodeStore();
	const [clickable, setClickable] = useState(true);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: KSK2110_R01InspectList,
		defaultGridData: defaultKSK2110_R01InspectRow,
		selectedRow: selectedKSK2110_R01InspectList,
		originalGridData: originalKSK2110_R01InspectList,
		updateRow: (index: number, data: any) => updateKSK2110_R01InspectList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		badCaseNm: '불량사유',
		badQty: '불량수량',
	}

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<KSK2110_R01InspectDto>({
			idKey: 'valueIndex',
			selectedList: selectedKSK2110_R01InspectList,
			deletedList: deletedKSK2110_R01InspectList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});

		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [
		selectedKSK2110_R01InspectList, deletedKSK2110_R01InspectList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	]);

	const commonInputColumns = ['badCase', 'badQty'];
	const defaultEditableColumns = ['badCase', 'badQty'];

	const handleGridDropDownCommitAfter = useMemo(() =>
		createGridDropDownCommitAfter<KSK2110_R01InspectDto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const baseColumnDefs: ColDef<KSK2110_R01InspectDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
        {
            headerName: '불량사유', colId: 'badCase', field: 'badCase', width: 130,
			...CommonCodeColumn.options(faultyValueList, true, CommonCodeColumn.change(updateKSK2110_R01InspectList), handleGridDropDownCommitAfter),
		},
        {
            headerName: '불량수량', colId: 'badQty', field: 'badQty', width: 130,
			editable: true,
			pasteable: true,
		},
		// ...IndexParameterCell(),
	], [deletedKSK2110_R01InspectList, KSK2110_R01InspectList, faultyValueList, updateKSK2110_R01InspectList, handleGridDropDownCommitAfter]);

	const columnDefs: ColDef<KSK2110_R01InspectDto>[] = useMemo(() => {
		if (inspectColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<KSK2110_R01InspectDto>({
			baseColumnDefs,
			columnState: inspectColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, inspectColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: KSK2110_R01InspectList,
		originalDataList: originalKSK2110_R01InspectList,
		updateRow: updateKSK2110_R01InspectList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: KSK2110_R01InspectList,
		idKey: 'valueIndex',
		selectedList: selectedKSK2110_R01InspectList,
		deletedList: deletedKSK2110_R01InspectList,
		addSelected: addSelectedKSK2110_R01InspectList,
		removeSelected: removeSelectedKSK2110_R01InspectList,
		clickable, setClickable
	});

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: KSK2110_R01InspectList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<KSK2110_R01InspectDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				// 입고량과 비고는 편집 가능한 상태일 때만 클릭으로 편집 시작
				if (field === 'updateTransportQty' || field === 'remark') {
					return commonInputColumns.includes(field);
				}
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(KSK2110_R01InspectList, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK2110_R01InspectDto>[] = useMemo(() => [
	], [editKSK2110_R01?.itemCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(KSK2110_R01InspectList, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<KSK2110_R01InspectDto>) => {
		postSortHandler(params, KSK2110_R01InspectList, originalKSK2110_R01InspectList, setKSK2110_R01InspectList);
	}, [KSK2110_R01InspectList, originalKSK2110_R01InspectList, ]);

	const pinnedBottomRowData = useMemo(() => {

		const totalQty : number = KSK2110_R01InspectList
			.reduce((sum, item) => sum + (deletedKSK2110_R01InspectList.includes(item.itemCd) ? 0 : Number(item.badQty) || 0), 0);

		return [{
			badCaseNm: KSK2110_R01InspectList.length.toLocaleString() + "건",
			badQty: totalQty,
		}];	  
	}, [KSK2110_R01InspectList, deletedKSK2110_R01InspectList,]);

	return (
		<>
			{/* selectedDetailKSK2110_R01 : {JSON.stringify(selectedKSK2110_R01InspectList)}<br /> */}
			
			<GridContextWrapper<KSK2110_R01InspectDto>
				gridWrapperRef={gridWrapperRef}
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setKSK2110_R01InspectList}
				initSelected={initSelectedKSK2110_R01InspectList}
				defaultRowData={defaultKSK2110_R01InspectRow}
				baseColumnDefs={columnDefs}	
				setColumnState={setInspectColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<KSK2110_R01InspectDto>
					ref={gridRef}
					rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
					columnDefs={columnDefs}		
					defaultColDef={gridDefaultColDef}

					rowClassRules={createRowClassRules<KSK2110_R01InspectDto>({
						idKey: 'valueIndex',
						selectedList: selectedKSK2110_R01InspectList,
						deletedList: deletedKSK2110_R01InspectList
					})}

					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					

					onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
					// Grid_Select에 공통 로직 처리를 위한 props 전달
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={setInspectColumnState}		//컬럼 상태 저장
				/>
			</GridContextWrapper>
		</>
	)
}